/**
 * YesNoEdge
 *
 * Renders the Yes / No branch lines coming out of every DecisionNode.
 * The label pill is color-coded to indicate whether the branch is a
 * positive (green) or negative (red) outcome:
 *   - "Yes" → green pill  (outcome is good — check passed)
 *   - "No"  → red pill    (outcome is bad  — check failed)
 *
 * Where it appears in the flowchart (every decision node uses this edge type):
 *   [Identity Proof Valid?] ──Yes (green)──> [next check or Clear terminal]
 *                           ──No  (red)───>  [Needs Review / Error terminal]
 *
 *   [Global Watchlist Record Found?] ──Yes (red)───> [Needs Review terminal]
 *                                    ──No  (green)──> [next check or Clear terminal]
 *
 * The label pill is rendered via EdgeLabelRenderer (HTML layer, outside SVG)
 * so full CSS styling applies. Path uses sharp right-angle routing (borderRadius: 0).
 *
 * Props are passed automatically by ReactFlow when registered as an edge type.
 * The `data.fieldType` prop must be set in utils.js when building decision edges.
 */
import React from 'react';

import { Box, Typography } from '@mui/material';
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath } from '@xyflow/react';
import PropTypes from 'prop-types';

import styles from '../../FlowChartCanvas.module.css';

import { LABELS } from '../../helper/constants';

function YesNoEdge({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style = {},
    markerEnd,
    label,
    data = {},
}) {
    // fieldType identifies which decision node this edge belongs to,
    // used to determine whether Yes/No color meanings should be inverted.
    const fieldType = data?.fieldType || '';

    // Compute the SVG path and the midpoint coordinates for label placement
    const [edgePath, labelX, labelY] = getSmoothStepPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
        borderRadius: 0, // sharp corners to match the flowchart's right-angle style
    });

    // For global watchlist and 7 year criminal record decisions, "Yes" means a record was found
    // (a bad outcome), so swap the green/red label colors.
    const isInvertedDecision = [
        LABELS.GLOBAL_WATCHLIST_RECORD_FOUND,
        LABELS.SEVEN_YEAR_CRIMINAL_RECORD_FOUND,
    ].includes(fieldType);

    let labelClassName = label === 'Yes' ? styles.yesEdgeLabel : styles.noEdgeLabel;
    if (isInvertedDecision) {
        labelClassName = label === 'Yes' ? styles.noEdgeLabel : styles.yesEdgeLabel;
    }

    return (
        <>
            {/* The actual SVG line with optional arrow marker */}
            <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />

            {/* Label rendered in the HTML layer (outside SVG) so CSS styling applies fully */}
            {label && (
                <EdgeLabelRenderer>
                    <Box
                        style={{
                            position: 'absolute',
                            // Center the label pill exactly at the path midpoint
                            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                            pointerEvents: 'none',
                            zIndex: 10,
                        }}
                    >
                        <Typography component="span" className={labelClassName}>
                            {label}
                        </Typography>
                    </Box>
                </EdgeLabelRenderer>
            )}
        </>
    );
}

YesNoEdge.propTypes = {
    sourceX: PropTypes.number.isRequired,
    sourceY: PropTypes.number.isRequired,
    targetX: PropTypes.number.isRequired,
    targetY: PropTypes.number.isRequired,
    sourcePosition: PropTypes.string.isRequired,
    targetPosition: PropTypes.string.isRequired,
    style: PropTypes.object,
    markerEnd: PropTypes.string,
    label: PropTypes.string,
    data: PropTypes.shape({
        fieldType: PropTypes.string,
    }),
};

export default YesNoEdge;
